import React from "react";
import { useForm, Controller } from "react-hook-form";
import { Run } from "../../lib/supabase";
import { ENTITY_NAMES } from "../../constants/entities";
import { Listbox } from "../ui/Listbox";

interface RunFormProps {
  defaultValues?: Partial<Run>;
  onSubmit: (data: Partial<Run>) => Promise<void>;
  isLoading: boolean;
  seriesId: string;
  onCancel?: () => void;
}

const RunForm: React.FC<RunFormProps> = ({
  defaultValues,
  onSubmit,
  isLoading,
  seriesId,
  onCancel,
}) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<Partial<Run>>({
    defaultValues: {
      name: "",
      day_of_week: [],
      start_time: "",
      end_time: "",
      start_date: new Date().toISOString().split("T")[0], // Default to today
      location: "",
      instructor: "",
      max_students: 20, // Default max students
      series_id: seriesId,
      active: true,
      price: 300.0, // Default price
      ...defaultValues,
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <label htmlFor="name" className="label">
          {ENTITY_NAMES.RUN} Name
        </label>
        <div className="mt-1">
          <input
            type="text"
            id="name"
            className={`input ${errors.name ? "input-error" : ""}`}
            {...register("name", { required: "Name is required" })}
          />
        </div>
        {errors.name && (
          <p className="mt-1 text-sm text-error-600">{errors.name.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="day_of_week" className="label">
          Days of Week
        </label>
        <div className="mt-1">
          <Controller
            name="day_of_week"
            control={control}
            rules={{
              required: "At least one day of week is required",
              validate: (value) =>
                (Array.isArray(value) && value.length > 0) ||
                "At least one day of week is required",
            }}
            render={({ field }) => (
              <Listbox
                options={[
                  { value: "Monday", label: "Monday" },
                  { value: "Tuesday", label: "Tuesday" },
                  { value: "Wednesday", label: "Wednesday" },
                  { value: "Thursday", label: "Thursday" },
                  { value: "Friday", label: "Friday" },
                  { value: "Saturday", label: "Saturday" },
                  { value: "Sunday", label: "Sunday" },
                ]}
                value={field.value || []}
                onChange={field.onChange}
                multiple={true}
                placeholder="Select days of the week..."
              />
            )}
          />
        </div>
        {errors.day_of_week && (
          <p className="mt-1 text-sm text-error-600">
            {errors.day_of_week.message}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="start_date" className="label">
          Start Date <span className="text-error-500">*</span>
        </label>
        <div className="mt-1">
          <input
            type="date"
            id="start_date"
            className={`input ${errors.start_date ? "input-error" : ""}`}
            {...register("start_date", {
              required: "Start date is required",
            })}
          />
        </div>
        {errors.start_date && (
          <p className="mt-1 text-sm text-error-600">
            {errors.start_date.message}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="start_time" className="label">
            Start Time
          </label>
          <div className="mt-1">
            <input
              type="time"
              id="start_time"
              className={`input ${errors.start_time ? "input-error" : ""}`}
              {...register("start_time", {
                required: "Start time is required",
              })}
            />
          </div>
          {errors.start_time && (
            <p className="mt-1 text-sm text-error-600">
              {errors.start_time.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="end_time" className="label">
            End Time
          </label>
          <div className="mt-1">
            <input
              type="time"
              id="end_time"
              className={`input ${errors.end_time ? "input-error" : ""}`}
              {...register("end_time", { required: "End time is required" })}
            />
          </div>
          {errors.end_time && (
            <p className="mt-1 text-sm text-error-600">
              {errors.end_time.message}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="instructor" className="label">
            Instructor
          </label>
          <div className="mt-1">
            <input
              type="text"
              id="instructor"
              className={`input ${errors.instructor ? "input-error" : ""}`}
              {...register("instructor")}
              placeholder="Optional"
            />
          </div>
        </div>

        <div>
          <label htmlFor="max_students" className="label">
            Max Students
          </label>
          <div className="mt-1">
            <input
              type="number"
              id="max_students"
              min="1"
              className={`input ${errors.max_students ? "input-error" : ""}`}
              {...register("max_students", {
                min: { value: 1, message: "Must be at least 1" },
                valueAsNumber: true,
              })}
            />
          </div>
          {errors.max_students && (
            <p className="mt-1 text-sm text-error-600">
              {errors.max_students.message}
            </p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="location" className="label">
          Location
        </label>
        <div className="mt-1">
          <input
            type="text"
            id="location"
            className={`input ${errors.location ? "input-error" : ""}`}
            {...register("location")}
            placeholder="Optional"
          />
        </div>
      </div>

      <div>
        <label htmlFor="price" className="label">
          Price <span className="text-error-500">*</span>
        </label>
        <div className="mt-1">
          <input
            type="number"
            id="price"
            step="0.01"
            min="0"
            className={`input ${errors.price ? "input-error" : ""}`}
            {...register("price", {
              required: "Price is required",
              min: { value: 0, message: "Price must be positive" },
              valueAsNumber: true,
            })}
          />
        </div>
        {errors.price && (
          <p className="mt-1 text-sm text-error-600">{errors.price.message}</p>
        )}
      </div>

      <div className="flex justify-end space-x-3">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-outline"
            disabled={isLoading}
          >
            Cancel
          </button>
        )}
        <button type="submit" className="btn btn-primary" disabled={isLoading}>
          {isLoading ? (
            <>
              <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
              {defaultValues ? "Updating..." : "Creating..."}
            </>
          ) : (
            <>
              {defaultValues ? "Update" : "Create"} {ENTITY_NAMES.RUN}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default RunForm;
